// MorningMode.jsx — the guided morning routine (shell + meds + prep + the branch
// wizard). One continuous, sequential screen, mounted full-screen over the tab
// shell (from App.jsx, like ChatFullPage). Fidelity pass against
// docs/pronto-prototype-spec.md ("## Morning") — presentation only; all flow logic
// and data wiring (meds server timer, prep steps, the started-brew transaction) is
// preserved. Note: the roaming meds pill is intentionally HIDDEN on this screen
// (App renders MedsTimerChip only in the tab shell, not on the Morning route).
//
// Layout top→bottom (prototype):
//   1. Weather header (sun icon · "46° & crisp" · droplet sub-line)
//   2. Branch subtitle — "Morning Mode · {branchSub}"
//   3. Checklist — Mackenzie's meds · Take your SPLIT · Fill water · Fresh filter
//   4. Recipe card — the one-card branch wizard (<MorningCoffee/>)

import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { ChevronLeft } from "lucide-react";
import { GlassCard } from "./pronto";
import { medsIsRunning, formatCountdown, medsRemainingMs } from "../lib/morning";
import { getWeather } from "../lib/weather";
import { MorningCoffee } from "./MorningCoffee";
import { petMedsLabel, selfMedsLabel } from "../lib/household.js";

function conditionWord(t) {
  return t < 40 ? "cold" : t < 60 ? "crisp" : t < 75 ? "pleasant" : t < 85 ? "warm" : "hot";
}

// Branch → the subtitle fragment shown after "Morning Mode ·".
const BRANCH_SUB = {
  repeat: "you liked yesterday",
  tweak: "yesterday was 👎",
  new_bag: "new bag scanned",
  blend: "finishing the remnant",
};

// The prototype's combined sun+cloud glyph (SF-symbol stand-in).
function SunCloud() {
  return (
    <svg width="34" height="34" viewBox="0 0 24 24" fill="none" strokeLinecap="round" aria-hidden="true" className="flex-shrink-0">
      <circle cx="8.5" cy="8" r="3" fill="#E0A32C" />
      <g stroke="#E0A32C" strokeWidth="1.5">
        <line x1="8.5" y1="2" x2="8.5" y2="3" />
        <line x1="3.4" y1="3.4" x2="4.3" y2="4.3" />
        <line x1="2" y1="8" x2="3" y2="8" />
        <line x1="12.7" y1="3.4" x2="13.6" y2="4.3" />
      </g>
      <path d="M8 19h9.2a3.4 3.4 0 0 0 .3-6.8 4.5 4.5 0 0 0-8.7-1.3A3.6 3.6 0 0 0 8 19z" fill="#C2C7D0" />
    </svg>
  );
}

function WeatherHeader({ weather }) {
  const temp = weather?.temp_f;
  const feels = weather?.feels_f;
  const precip = weather?.peak_3h_precip_pct;
  const cond = temp != null ? conditionWord(temp) : null;
  const dayName = new Date().toLocaleDateString("en-US", { weekday: "long" });
  return (
    <>
      <div className="flex items-center gap-[11px] mt-0 mb-0.5 pr-[84px]">
        <SunCloud />
        <h1 className="font-round font-bold text-[32px] leading-none tracking-[-0.02em] text-pronto-ink whitespace-nowrap">
          {temp != null ? (
            <>{temp}°{cond && <span className="text-[19px] font-semibold text-pronto-text-2"> &amp; {cond}</span>}</>
          ) : "Buongiorno"}
        </h1>
      </div>
      <div className="flex items-center gap-1.5 font-sf text-[13.5px] text-pronto-text-2 mb-1" aria-hidden="true">
        <svg width="13" height="13" viewBox="0 0 24 24" fill="#6E8CB8" className="flex-shrink-0">
          <path d="M12 2.7S5.8 10 5.8 15a6.2 6.2 0 0 0 12.4 0C18.2 10 12 2.7 12 2.7z" />
        </svg>
        {weather && feels != null ? `Feels like ${feels}° · ${precip}% rain · ${dayName}` : dayName}
      </div>
    </>
  );
}

// One checklist row — the prototype's 16px rounded-square checkbox + label + an
// optional trailing state string. `live` rows toggle; the checkbox style is the
// same whether the row is interactive or a settled ✓.
function CheckRow({ checked, onToggle, label, trailing, trailingColor, divider = true, disabled }) {
  const box = (
    <span
      className="w-4 h-4 rounded-[5px] grid place-items-center flex-shrink-0 text-[10px]"
      style={
        checked
          ? { background: "#1C7268", color: "#fff", border: "2px solid #1C7268" }
          : { background: "rgba(255,255,255,0.7)", border: "2px solid #B7BCC6", color: "transparent" }
      }
    >
      ✓
    </span>
  );
  const inner = (
    <>
      {box}
      <span className="flex-1 font-sf text-[13.5px] text-pronto-ink">{label}</span>
      {trailing != null && trailing !== "" && (
        <span className="font-sf text-[12px] font-bold flex-shrink-0" style={{ color: trailingColor }}>
          {trailing}
        </span>
      )}
    </>
  );
  const cls = `flex items-center gap-2.5 px-[14px] py-1 min-h-[38px] ${divider ? "border-b-[0.5px] border-[rgba(60,60,67,0.08)]" : ""}`;
  return onToggle ? (
    <button onClick={onToggle} disabled={disabled} className={`w-full text-left ${cls} disabled:opacity-60`}>
      {inner}
    </button>
  ) : (
    <div className={cls}>{inner}</div>
  );
}

export function MorningMode({ store, onExit }) {
  const session = store.morningSession;
  const prefs = store.preferences;
  const [now, setNow] = useState(() => new Date());
  const [busy, setBusy] = useState(false);
  const [branch, setBranch] = useState("repeat");
  const [weather, setWeather] = useState(null);

  useEffect(() => {
    if (!session) store.startMorningSession().catch((e) => console.warn("start morning failed", e?.message));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    let cancelled = false;
    getWeather().then((w) => { if (!cancelled) setWeather(w); }).catch(() => {});
    return () => { cancelled = true; };
  }, []);

  // Local tick so the inline meds countdown updates while the flow is open.
  useEffect(() => {
    if (!session?.meds_given_at) return;
    const iv = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(iv);
  }, [session?.meds_given_at]);

  const petLabel = petMedsLabel(prefs);
  const selfLabel = selfMedsLabel(prefs);
  const medsGiven = !!session?.meds_given_at;
  const medsRunning = medsGiven && medsIsRunning(session, prefs, now);
  const medsLeft = medsGiven ? medsRemainingMs(session, prefs, now) : null;
  const prep = session?.prep || {};
  const completed = !!session?.completed_at;

  const toggleMeds = async () => {
    if (busy) return;
    setBusy(true);
    try {
      if (medsGiven) await store.clearMedsGiven();
      else await store.setMedsGiven(activeBeanName(store));
    } finally { setBusy(false); }
  };
  const togglePrep = (key) => store.setPrepStep(key, !prep[key]).catch((e) => console.warn(e?.message));

  const medsTrail = medsRunning
    ? `🐾 ${formatCountdown(medsLeft)} ›`
    : medsGiven ? "🐾 ready ›" : "Tap: Given";

  return (
    <div className="min-h-screen bg-pronto-page font-sf">
      <header className="sticky top-0 z-10 flex items-center gap-2 px-5 py-3 bg-[var(--pg-6)] backdrop-blur-[24px] border-b border-[var(--pg-border)] safe-top">
        <button onClick={onExit} className="p-1.5 -ml-1.5 rounded-lg text-pronto-text-2 active:bg-black/5" aria-label="Back to Today">
          <ChevronLeft size={22} />
        </button>
        <div className="font-round font-bold text-[16px] text-pronto-ink">Morning</div>
      </header>

      <main className="max-w-[600px] mx-auto px-5 pt-0 pb-24">
        {/* 1. Weather header */}
        <WeatherHeader weather={weather} />

        {/* 2. Branch subtitle */}
        <div className="font-sf text-[12px] font-semibold text-pronto-navy mb-[14px]">
          Morning Mode · {BRANCH_SUB[branch] || BRANCH_SUB.repeat}
        </div>

        {/* 3. Checklist */}
        <GlassCard radius={18} className="overflow-hidden mb-1.5">
          {/* Both rows are hidden when unconfigured. A generic "Pet's
              medication" for someone with no pet reads as a bug, not a prompt. */}
          {petLabel && (
            <CheckRow
              checked={medsGiven}
              onToggle={toggleMeds}
              disabled={busy}
              label={petLabel}
              trailing={medsTrail}
              trailingColor={medsGiven ? "#1C7268" : "#8A8A8F"}
            />
          )}
          {selfLabel && (
            <CheckRow
              checked={!!prep.split_dose}
              onToggle={() => togglePrep("split_dose")}
              label={selfLabel}
              trailing={prep.split_dose ? "" : "Tap"}
              trailingColor={prep.split_dose ? "#1C7268" : "#8A8A8F"}
            />
          )}
          <CheckRow
            checked={prep.water !== false}
            onToggle={() => togglePrep("water")}
            label="Fill water chamber"
          />
          <CheckRow
            checked={prep.filter !== false}
            onToggle={() => togglePrep("filter")}
            label="Fresh filter in the bin"
            divider={false}
          />
        </GlassCard>

        {/* 4. Recipe card — one-card branch wizard. Complete state lives inside. */}
        {completed ? (
          <MorningComplete store={store} session={session} branch={branch} onExit={onExit} />
        ) : (
          <MorningCoffee store={store} session={session} weather={weather} onResolvedBranch={setBranch} onComplete={() => {}} />
        )}
      </main>
    </div>
  );
}

// "Morning complete" — the prototype's teal completion block (rendered in place of
// the recipe card once the brew is logged and completeMorning stamped completed_at).
function MorningComplete({ store, session, branch, onExit }) {
  const foot = {
    repeat: "Zero decisions · no AI call",
    tweak: "Rating prompt in ~40 min",
    new_bag: "Depletion tracking starts now",
    blend: "Decrements both bags by share",
  }[branch] || "";
  const log = (store.brewLogs || []).find((l) => l.id === session?.brew_log_id) || (store.brewLogs || [])[0];
  const doseG = log?.dose_g != null ? Math.round(log.dose_g) : null;
  return (
    <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}>
      <GlassCard radius={24} elevated className="p-[18px]">
        <div className="rounded-[16px] bg-[rgba(46,158,143,0.1)] p-[14px] text-center">
          <div className="font-sf text-[14px] font-bold text-[#1C7268]">☕ Morning complete</div>
          <div className="font-sf text-[12px] text-[#1C7268] mt-[3px] leading-[1.4]">
            Logged{doseG != null ? ` · bag −${doseG}g` : ""} · rating in ~40 min
          </div>
          <div className="flex gap-2 mt-3">
            <button
              onClick={onExit}
              className="flex-1 h-10 rounded-[11px] bg-pronto-navy text-white font-sf text-[13.5px] font-semibold"
            >
              Back to Today
            </button>
            <button
              onClick={() => { window.location.hash = "listen"; }}
              className="flex-1 h-10 rounded-[11px] bg-white/85 text-pronto-navy font-sf text-[13.5px] font-semibold flex items-center justify-center gap-1.5"
            >
              <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M6 4l14 8-14 8z" /></svg>
              Briefing
            </button>
          </div>
          <button
            onClick={() => { window.location.hash = "coffee"; }}
            className="mt-2.5 font-sf text-[12px] font-semibold text-[#1C7268]"
          >
            View in brew history ›
          </button>
        </div>
        {foot && <div className="text-center font-sf text-[12px] text-pronto-text-2 mt-[9px]">{foot}</div>}
      </GlassCard>
    </motion.div>
  );
}

// Best-effort name of the bag being brewed, for the meds push copy.
function activeBeanName(store) {
  const openIds = store.preferences?.open_bean_ids;
  const beans = store.beans || [];
  const bean = (Array.isArray(openIds) && openIds.length
    ? beans.find((b) => openIds.includes(b.id))
    : beans.find((b) => b.status !== "finished")) || beans[0];
  return bean?.name || "";
}
